<template>
  <footer
    :key="key"
    class="relative flex justify-between w-full gap-1 px-4 pt-2"
    v-if="showFooter"
    :style="{
      paddingBottom: parseInt($helpers.safeZone('bottom')) === 0 ? `16px` : $helpers.safeZone('bottom')
    }"
  >
    <div class="absolute top-0 inset-x-4 h-px2 bg-platinum"></div>
    <slot>
      <div
        v-for="item in slicedMenu.filter(el => el.type !== 'installIos' && el.type !== 'installAndroid')"
        :ref="`${item.type}.${item.app_module_id}`"
        :key="item.app_module_id"
        @click="goToMenu(item)"
        class="flex justify-center w-full max-w-17"
        :class="{'text-gray': !$route.fullPath.split('/').includes(String(item.app_module_id))}"
        :style="$route.fullPath.split('/').includes(String(item.app_module_id)) ? {color: $helpers.getCorrectColor(themeColorHEX)} : {}"
      >
        <t-image-text class="items-center w-full">
          <div class="relative w-6 h-6">
            <div class="w-full h-full" v-html="item.icon" />
            <div
              class="absolute flex items-center justify-center px-1 font-semibold text-white rounded-full -top-1 -right-2 bg-red text-12 min-w-5 min-h-5"
              v-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'number'"
            >
              {{ item.bubble.length > 99 ? '99+' : item.bubble.length }}
            </div>
            <div
              class="absolute rounded-full -top-1 -right-1 bg-red min-w-2 min-h-2"
              v-else-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'dot'"
            />
          </div>
          <t-text
            size="sm"
            class="overflow-hidden duration-200 text-ellipsis max-w-[72px] whitespace-nowrap"
            style="color: inherit !important"
            >{{ item.name }}</t-text
          >
        </t-image-text>
      </div>
      <t-image-text
        v-if="showMore"
        ref="more"
        @click.native="$refs['more-curtain'].show()"
        class="flex items-center justify-center w-full cursor-pointer max-w-17"
        :class="{'text-gray': !isActiveMore}"
        :style="isActiveMore ? {color: $helpers.getCorrectColor(themeColorHEX)} : {}"
      >
        <div class="relative">
          <ph-list :size="24" class="duration-200" />
          <div
            class="absolute flex items-center justify-center px-1 font-semibold text-white rounded-full -top-1 -right-2 bg-red text-12 min-w-5 min-h-5"
            v-if="prioritizedBubble !== 0 && prioritizedBubbleType === 'number'"
          >
            {{ prioritizedBubble > 99 ? '99+' : prioritizedBubble }}
          </div>
          <div
            class="absolute rounded-full -top-1 -right-1 bg-red min-w-2 min-h-2"
            v-else-if="prioritizedBubble !== 0 && prioritizedBubbleType === 'dot'"
          />
        </div>
        <t-text size="sm" class="duration-200 text-ellipsis" style="color: inherit !important">{{ $t('components.footer.more') }}</t-text>
      </t-image-text>
      <t-image-text
        class="items-center w-full"
        v-else-if="!showMore && userHideAndroidAlert && installAndroid"
        @click.native="handleInstallPrompt({platform: 'android', show: true})"
      >
        <div class="w-8 h-8" v-html="installAndroid.icon" />
        <t-text
          size="sm"
          class="overflow-hidden font-semibold duration-200 text-ellipsis max-w-[72px] whitespace-nowrap"
          style="color: inherit !important"
          >{{ installAndroid.name }}</t-text
        >
      </t-image-text>
      <t-image-text
        class="items-center w-full"
        v-else-if="!showMore && userHideIosAlert && installIos"
        @click.native="handleInstallPrompt({platform: 'ios', show: true})"
      >
        <div class="w-8 h-8" v-html="installIos.icon" />
        <t-text
          size="sm"
          class="overflow-hidden font-semibold duration-200 text-ellipsis max-w-[72px] whitespace-nowrap"
          style="color: inherit !important"
          >{{ installIos.name }}</t-text
        >
      </t-image-text>

      <t-curtain ref="more-curtain" v-if="showMore">
        <t-curtain-tab name="tab-more" :title="$t('components.footer.more')">
          <t-list-item
            v-for="item in slicedMore.filter(el => el.type !== 'installIos' && el.type !== 'installAndroid')"
            :key="item.app_module_id"
            @click.native="useLink(item)"
            :text="item.name"
            :iconSrc="item.icon"
            :bubble="item.bubble.length"
            :bubbleType="$helpers.getBubbleTypeFromMenuType(item.type)"
            class="cursor-pointer"
            :active="$route.fullPath === `/${item.type}/${item.app_module_id}`"
          />
          <t-list-item
            v-if="userHideAndroidAlert && installAndroid"
            :text="installAndroid.name"
            @click.native="
              $refs['more-curtain'].hide()
              handleInstallPrompt({platform: 'android', show: true})
            "
            :iconSrc="installAndroid.icon"
            class="cursor-pointer"
          />
          <t-list-item
            v-else-if="userHideIosAlert && installIos"
            :text="installIos.name"
            @click.native="
              $refs['more-curtain'].hide()
              handleInstallPrompt({platform: 'ios', show: true})
            "
            :iconSrc="installIos.icon"
            class="cursor-pointer"
          />
        </t-curtain-tab>
      </t-curtain>
    </slot>
  </footer>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  import handleViewport from '@/utils/mixins/handleViewport'
  export default {
    name: 't-footer',
    mixins: [handleViewport],
    computed: {
      ...mapGetters('user', ['menuLoaded', 'userMenu', 'userHideIosAlert', 'userHideAndroidAlert']),
      ...mapGetters(['themeColorHEX']),
      slicedMenu() {
        return this.showMore ? this.userMenu.slice(0, 4) : this.userMenu.slice(0, 5)
      },
      slicedMore() {
        return this.userMenu.slice(4)
      },
      installIos() {
        return this.userMenu.find(el => el.type === 'installIos')
      },
      installAndroid() {
        return this.userMenu.find(el => el.type === 'installAndroid')
      },
      showMore() {
        return this.userMenu.length > 4
      },
      isActiveMore() {
        return !!this.slicedMore.find(el => {
          return this.$route.fullPath.split('/').includes(String(el.app_module_id))
        })
      },
      prioritizedBubble() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.slicedMore)
        const bubble = this.slicedMore.find(el => el.type === prioritizedBubble)
        if (bubble) return bubble.bubble.length
        else return 0
      },
      prioritizedBubbleType() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.slicedMore)
        const bubble = this.slicedMore.find(el => el.type === prioritizedBubble)
        if (bubble) return this.$helpers.getBubbleTypeFromMenuType(bubble.type)
        else return 'dot'
      }
    },
    data() {
      return {
        showFooter: false,
        key: 0
      }
    },
    async created() {
      await this.menuLoaded
      this.showFooter = true
    },
    methods: {
      ...mapMutations('user', ['handleInstallPrompt']),
      ...mapMutations('layout', ['emitContentReload']),
      useLink(item) {
        this.hideCurtain()
        setTimeout(() => {
          this.$router.push({name: `${item.type}.index`, params: {app_module_id: item.app_module_id}})
        }, 200)
      },
      goToMenu(item) {
        if (this.$route.path.split('/').includes(String(item.app_module_id))) return this.emitContentReload()
        this.$router.push({
          name: `${item.type}.index`,
          params: {
            app_module_id: item.app_module_id
          }
        })
      },
      hideCurtain() {
        this.$refs['more-curtain'].hide()
      },
      onDesktopViewEnter() {
        this.updateFooter()
      },
      onMobileViewEnter() {
        this.updateFooter()
      },
      updateFooter() {
        this.key++
      }
    }
  }
</script>
